import { useEffect, useState } from "react";
import { useDashboard } from "./hooks/useDashboard";
import { Sidebar } from "./components/Sidebar";
import { Header } from "./components/Header";
import { ZoneDetail } from "./components/ZoneDetail";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Overview } from "./views/Overview";
import { Zones } from "./views/Zones";
import { Pump } from "./views/Pump";
import { Schedule } from "./views/Schedule";
import { Activity } from "./views/Activity";
import { Settings } from "./views/Settings";
import { IS_MOCK } from "./api/client";
import { C, hexA } from "./theme";
import { computeNextRun } from "./lib/nextRun";
import type { ActivityFilter, ViewKey } from "./types";

export function App() {
  const { state, error, syncing, pending, actions } = useDashboard();
  const [view, setView] = useState<ViewKey>("overview");
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState<ActivityFilter>("all");
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!state) {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: "100vh", color: C.textMid }}>
        <div style={{ textAlign: "center" }}>
          <div className="serif" style={{ fontSize: 22, color: C.ink }}>Marram</div>
          <div style={{ marginTop: 6, fontSize: 13 }}>{error ? `Connection issue — ${error}` : "Connecting to controller…"}</div>
        </div>
      </div>
    );
  }

  const selectedZone = state.zones.find((z) => z.id === selected) ?? null;
  const nr = computeNextRun(state);
  const activeCount = state.zones.filter((z) => z.state === "watering" || z.state === "queued").length;

  return (
    <div className="app-shell">
      <div className="sidebar-wrap">
        <Sidebar view={view} onNav={setView} controller={state.controller} />
      </div>

      <main className="main-col">
        <Header
          view={view}
          now={now}
          weather={state.weather}
          price={state.price}
          syncedMinsAgo={state.syncedMinsAgo}
          paused={state.paused}
          syncing={syncing}
          activeCount={activeCount}
          nextName={nr.kind === "next" ? nr.name : null}
          canRunNext={nr.kind === "next" && !!nr.zone}
          runNextBusy={!!(nr.zone && pending[`run:${nr.zone.id}`])}
          stopAllBusy={!!pending["stop:all"]}
          onRunNext={() => nr.zone && actions.runZone(nr.zone.id, nr.zone.dur)}
          onStopAll={actions.stopAll}
          onSync={actions.sync}
          onToggleRainDelay={() => actions.setRainDelay(!state.paused)}
          onNav={setView}
        />

        {IS_MOCK && (
          <div
            role="note"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 34px",
              background: hexA(C.beacon, 0.08),
              borderBottom: `1px solid ${hexA(C.beacon, 0.19)}`,
              color: C.beaconDeep,
              fontSize: 12.5,
            }}
          >
            Demo data — not connected to the controller. Live weather, ET₀ and prices appear when this build points at the Worker (VITE_API_BASE).
          </div>
        )}

        {state.paused && (
          <div
            role="status"
            aria-live="polite"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 34px",
              background: hexA(C.beacon, 0.1),
              borderBottom: `1px solid ${hexA(C.beacon, 0.19)}`,
              color: C.beaconDeep,
              fontSize: 12.5,
            }}
          >
            Rain delay is on — automatic watering is paused. Manual runs still work.
          </div>
        )}

        {error && (
          <div role="alert" style={{ padding: "8px 34px", background: hexA(C.coral, 0.08), color: C.coralDeep, fontSize: 12.5 }}>
            {error} — showing last known state.
          </div>
        )}

        <div className="content">
          <ErrorBoundary key={view}>
          {view === "overview" && (
            <Overview
              state={state}
              pending={pending}
              onRun={actions.runZone}
              onStop={actions.stopZone}
              onStep={actions.setDuration}
              onToggle={actions.setEnabled}
              onSelect={setSelected}
              onNav={setView}
            />
          )}
          {view === "zones" && (
            <Zones
              state={state}
              pending={pending}
              onRun={actions.runZone}
              onStop={actions.stopZone}
              onStep={actions.setDuration}
              onToggle={actions.setEnabled}
              onSelect={setSelected}
            />
          )}
          {view === "pump" && <Pump pump={state.pump} paused={state.paused} source={state.sources?.pump} zones={state.zones} />}
          {view === "schedule" && <Schedule state={state} />}
          {view === "activity" && <Activity feed={state.feed} filter={filter} onFilter={setFilter} />}
          {view === "settings" && <Settings state={state} />}
          </ErrorBoundary>
        </div>
      </main>

      <ZoneDetail
        zone={selectedZone}
        weather={state.weather}
        pending={pending}
        onClose={() => setSelected(null)}
        onRun={actions.runZone}
        onStop={actions.stopZone}
        onToggle={actions.setEnabled}
      />
    </div>
  );
}
